/**
 * Feature flag context.
 *
 * Fetches the backend's current feature set on boot and exposes a simple
 * `useFeature('id')` hook plus a dev-only override mechanism so we can
 * preview the production feature lock without deploying.
 *
 * Override flow:
 *   1. User navigates to any URL with `?features=prod` → override = "prod"
 *      → shows only the prod-allowed feature set (same as fedbd.ai will show).
 *   2. User navigates with `?features=dev` → override cleared.
 *   3. Override persists in localStorage (keyed by `feature_override`) so the
 *      preview matches what the customer will see across navigation.
 *   4. Override is a no-op in actual production (backend already locked).
 *
 * Production-hardcoded feature IDs live in PROD_FEATURES below — they must
 * match `FEATURES_ENABLED` in the production backend's .env.
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'feature_override';

// Must mirror backend prod .env → FEATURES_ENABLED
const PROD_FEATURES = ['opportunity_intake', 'document_ingestion'];

const FeatureContext = createContext(null);

function readOverride() {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v === 'prod' || v === 'dev' ? v : null;
  } catch {
    return null;
  }
}

function writeOverride(v) {
  try {
    if (v === null) localStorage.removeItem(LS_KEY);
    else localStorage.setItem(LS_KEY, v);
  } catch {
    // ignore storage failures (private mode, quotas)
  }
}

export function FeatureProvider({ children }) {
  // Server-reported config. Starts null so consumers can render a
  // loading-safe default (assume nothing is enabled yet) until the fetch
  // completes.
  const [serverConfig, setServerConfig] = useState(null);
  const [override, setOverride] = useState(() => readOverride());
  const [isLoaded, setIsLoaded] = useState(false);

  // 1. Read `?features=` on every mount and update persistent override.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qp = params.get('features');
      if (qp === 'prod' || qp === 'dev') {
        writeOverride(qp === 'dev' ? null : 'prod');
        setOverride(qp === 'dev' ? null : 'prod');
        // Strip the query param so refresh/bookmark doesn't loop it.
        params.delete('features');
        const newSearch = params.toString();
        const newUrl =
          window.location.pathname +
          (newSearch ? `?${newSearch}` : '') +
          window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch {
      // Ignore URL parsing failures
    }
  }, []);

  // 2. Fetch backend feature config on boot.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/config/features`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('feature config fetch failed');
        const data = await res.json();
        if (!cancelled) {
          setServerConfig(data);
        }
      } catch (err) {
        // Fallback: assume dev mode with every feature on so we don't
        // accidentally lock the user out because of a transient fetch error.
        if (!cancelled) {
          console.warn('[features] using dev fallback:', err?.message);
          setServerConfig({ mode: 'dev', features: null, all_features: {} });
        }
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearOverride = useCallback(() => {
    writeOverride(null);
    setOverride(null);
  }, []);

  const setProdOverride = useCallback(() => {
    writeOverride('prod');
    setOverride('prod');
  }, []);

  // Effective enabled set:
  //   - Real prod → whatever backend reports (already enforced server-side).
  //   - Dev/staging with "prod" override → use PROD_FEATURES (client-only
  //     simulation; backend still serves everything here).
  //   - Dev/staging without override → backend-reported set (all features).
  const enabledSet = useMemo(() => {
    if (!serverConfig) return new Set();
    const mode = serverConfig.mode;
    if (mode === 'production') {
      return new Set(serverConfig.features || []);
    }
    if (override === 'prod') {
      return new Set(PROD_FEATURES);
    }
    if (Array.isArray(serverConfig.features)) {
      return new Set(serverConfig.features);
    }
    // Unknown (null) → assume everything is allowed
    return new Set([
      'opportunity_intake',
      'document_ingestion',
      'go_no_go',
      'gap_analysis',
      'compliance_tracker',
      'proposal_workspace',
      'submission_guide',
      'sentinel',
      'knowledge_base',
      'portfolio_velocity',
      'capability_matrix',
      'rtm',
      'bid_scorecard',
    ]);
  }, [serverConfig, override]);

  const isFeatureEnabled = useCallback(
    (featureId) => enabledSet.has(featureId),
    [enabledSet],
  );

  const value = useMemo(
    () => ({
      mode: serverConfig?.mode || 'dev',
      isLoaded,
      isProdOverride: override === 'prod' && serverConfig?.mode !== 'production',
      isProdLike:
        serverConfig?.mode === 'production' ||
        (override === 'prod' && serverConfig?.mode !== 'production'),
      isFeatureEnabled,
      enabledFeatures: Array.from(enabledSet).sort(),
      clearOverride,
      setProdOverride,
    }),
    [serverConfig, override, isLoaded, isFeatureEnabled, enabledSet, clearOverride, setProdOverride],
  );

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}

/**
 * Hook: useFeature('sentinel') → boolean
 * Safe to call before the config loads — returns false until loaded.
 */
export function useFeature(featureId) {
  const ctx = useContext(FeatureContext);
  if (!ctx) return false;
  return ctx.isFeatureEnabled(featureId);
}

export function useFeatures() {
  const ctx = useContext(FeatureContext);
  if (!ctx) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return ctx;
}
